import { useQuery } from '@apollo/client/react';
import { faCommentPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { chatListUnreadSubscribeToMore } from '../../apollo/merge';
import {
  App,
  ChatRoomKind,
  ChatRoomsOrder,
  ChatsDocument,
  OrderDirection,
} from '../../graphql/types';
import { notEmpty } from '../../utils/notEmpty';
import {
  EventCategories,
  EventLabels,
  EventSubCategories,
} from '../../utils/trackingEvents';
import { useChatContext } from '../Chat/Context';
import { ButtonSize } from '../reusable/Button';
import { IconButton } from '../reusable/Button/IconButton';
import Text, { TextKind } from '../reusable/Text';
import { ChatList } from './ChatList';

interface MultiChatListProps {
  onClick: () => void;
}

export const MultiChatList: React.FC<MultiChatListProps> = ({ onClick }) => {
  const { openChat, chatListOpen, filterByUnread } = useChatContext();

  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<OrderDirection>(
    OrderDirection.Desc,
  );

  const { data, error, fetchMore, subscribeToMore, networkStatus } = useQuery(
    ChatsDocument,
    {
      variables: {
        input: {
          first: 15,
        },
        orderBy: {
          order: ChatRoomsOrder.LastMessageReceivedAt,
          direction: sortOrder,
        },
        filters: {
          userName: nameSearch,
          apps: [App.Heart],
          kinds: [ChatRoomKind.Direct],
          isArchived: false,
          hasUnreadMessages: filterByUnread ? true : undefined,
          memberHasSignedIn: true,
        },
      },
      notifyOnNetworkStatusChange: true,
    },
  );
  const chats = data?.self?.chatRooms.edges.map(({ node }) => node);

  useEffect(() => {
    const loadedChatIds =
      data?.self?.chatRooms.edges.map(({ node }) => node.id) || [];

    const subscriptions: Record<string, () => void> = {};

    loadedChatIds.forEach((chatId) => {
      subscriptions[chatId] = chatListUnreadSubscribeToMore(
        subscribeToMore,
        chatId,
      );
    });

    return () => {
      const unsubscribes = Object.values(subscriptions);
      if (!chatListOpen && unsubscribes.length > 0) {
        Object.values(subscriptions).forEach((unsubscribe) => unsubscribe());
      }
    };
  }, [chatListOpen, data?.self?.chatRooms.edges.map, subscribeToMore]);

  const selfId = data?.self?.id;
  const hasNextPage = Boolean(data?.self?.chatRooms?.pageInfo?.hasNextPage);

  const loadMore = async () => {
    await fetchMore({
      variables: {
        input: {
          first: 15,
          after: data?.self?.chatRooms?.pageInfo?.endCursor,
        },
      },
    });
  };

  const chatsAndMembers =
    chats
      ?.map((chat) => {
        const member = chat.users.edges.find(
          ({ node }) => node.id !== selfId,
        )?.node;

        if (!member || !chat) {
          return null;
        }
        return {
          chat,
          member,
        };
      })
      .filter(notEmpty) || [];

  const handleSortClick = () => {
    setSortOrder((prev) =>
      prev === OrderDirection.Desc ? OrderDirection.Asc : OrderDirection.Desc,
    );
  };

  return (
    <ChatList
      setNameSearch={setNameSearch}
      sortOrder={sortOrder}
      onSortClick={handleSortClick}
      error={error}
      networkStatus={networkStatus}
      loadMore={loadMore}
      hasNextPage={hasNextPage}
      chatsAndMembers={chatsAndMembers}
      selfId={selfId}
      onChatClick={onClick}
      Header={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text kind={TextKind.TitleM}>Chats</Text>
          <IconButton
            size={ButtonSize.Small}
            icon={<FontAwesomeIcon icon={faCommentPlus} fontSize={20} />}
            ariaLabel="create chat"
            onClick={() => openChat()}
            trackingData={{
              event_category: EventCategories.Chat,
              event_subcategory: EventSubCategories.ChatList,
              event_label: EventLabels.CreateChat,
            }}
          />
        </div>
      }
    />
  );
};
